import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import CreateIcon from '@material-ui/icons/Create';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import BarChartIcon from '@material-ui/icons/BarChart';
import SchoolIcon from '@material-ui/icons/School';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

import { SOLUTION } from './solutions';
import { USER_ACCESS_LEVEL, ENTERPRISE_PLAN_KEYS, DQ_PRO_PLAN_KEY, FEATURE_KEY, PRODUCT_KEY, LEARNCREATOR_START_PATH } from '../../utils/constants';
import { isFeatureEnabled, isProductEnabled } from '../../utils/productAccess';
import { getCountryLabel } from '../../utils/geo';
import { COS_PREVIEW_PATH } from '../../utils/cpci.util';

const countryName = (code) => getCountryLabel(code) || code || '';
const NATIONAL_OVERVIEW = '/dq_pro/national-dashboard/overview';

/**
 * Partner Workspace navigation.
 *
 * Structure and labels come from the PbDQ Navigation Bar sheet. Routes and gates
 * come from the sidebar as it works today — this is a restructure of a working
 * menu, not a new one, so nothing here invents a URL or loosens a permission.
 *
 * Row shape:
 *   code         the sheet's Level 1/2/3 code — a stable key, also the test hook
 *   label        static string, or (ctx) => string where the sheet varies it
 *   icon         Level 1 only; sub-items are text, matching the current sidebar
 *   path         an EXISTING route, or (ctx) => path, or null when not built
 *   solutions    the sheet's Solution Type — see SOLUTION_GATING_READY
 *   visibleWhen  (ctx) => bool. The gate that actually applies today
 *   children     Level 2 rows
 *   slideCode    links the row to its mock-up
 *   note         the sheet's Internal Note
 *
 * ctx is { authUser, insightsPath } — the same values the sidebar already reads.
 */

/** Rows the sheet removes from the current sidebar. Recorded, not silently dropped. */
export const PARTNER_WORKSPACE_REMOVED = [
    { label: 'Peers', reason: 'Remove' },
    { label: 'Your Programs > Assessments', reason: 'Remove — preview moves to Solutions > COS Children Assessment > Preview' },
    { label: 'Profile Icon > Reset Password', reason: 'Remove — moves inside Settings' },
];

export const PARTNER_WORKSPACE_NAV = [
    {
        code: 'PW_HO',
        /**
         * The onboarding tutorial's step-1 anchor. REQUIRED, not decorative.
         *
         * TutorialManager force-navigates to a step's `route` whenever the
         * current path differs, and step 1's route is /my_workspace. If this id
         * is missing the step can never find its target, never advances, and
         * every attempt to leave Home is pushed straight back — the whole
         * sidebar stops working. The School and Teacher sidebars carry the same
         * id on their Home row.
         */
        id: 'sidebar-home-link',
        label: 'Home',
        icon: HomeIcon,
        path: '/my_workspace',
        slideCode: 'PW-{P0|P1|LC}-Home',
        solutions: [SOLUTION.P0, SOLUTION.P1, SOLUTION.LC],
        visibleWhen: () => true,
        /**
         * One row, not three. The deck shows PW-P0-Home, PW-P1-Home and
         * PW-LC-Home as the SAME screen with different step content — so this is
         * one entry whose page varies, never three menu items.
         */
        note: 'Same screen, three solution variants of its step content.',
    },

    {
        code: 'PW_SO',
        label: 'Solutions',
        icon: CategoryIcon,
        // Parent navigates only when the drawer is collapsed; otherwise it expands.
        path: '/dq_pro/solutions',
        slideCode: 'CS-Solution-Overview',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
        /**
         * Common Section — the same pages in the Partner, School and Teacher
         * workspaces.
         *
         * Each solution carries an Overview and, for most, a Preview. Those are
         * Level 3 SIDEBAR ROWS, not tabs: the Navigation Bar sheet gives them
         * the Level 3 Code and Level 3 Name columns, and keeps Tab Code / Tab
         * Name as a separate pair that only the Settings page uses.
         *
         * COS Readiness Solution has an Overview and no Preview — the sheet
         * lists none for it.
         *
         * The Government Checklist appears here only — the School and Teacher
         * blocks of the sheet do not list it.
         */
        children: [
            {
                // Solutions' own Overview: Level 2, a sibling of the solutions
                // rather than a child of one. Distinct from each solution's own
                // Level 3 Overview below.
                code: 'PW_SO_OV', label: 'Overview', path: '/dq_pro/solutions/overview',
                slideCode: 'CS-Solution-Overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
            },
            {
                code: 'PW_SO_P1', label: 'COS Readiness Solution', path: '/dq_pro/solutions/readiness',
                slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'PW_SO_P1_OV', label: 'Overview', path: '/dq_pro/solutions/readiness/overview',
                        slideCode: 'CS-Solution-P1-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'PW_SO_P0', label: 'COS Children Assessment', path: '/dq_pro/solutions/children-assessment',
                slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'PW_SO_P0_OV', label: 'Overview', path: '/dq_pro/solutions/children-assessment/overview',
                        slideCode: 'CS-Solution-P0-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    {
                        // Sheet row 13: the old "Assessments > Preview Assessment"
                        // item is removed because it lives here instead.
                        code: 'PW_SO_P0_PV', label: 'Preview', path: '/dq_pro/solutions/children-assessment/preview',
                        // The path above redirects to the COS catalogue preview page.
                        matchPrefixes: [COS_PREVIEW_PATH],
                        slideCode: 'CS-Solution-P0-preview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
            {
                code: 'PW_SO_LC', label: 'LearnCreator', path: '/dq_pro/solutions/learncreator',
                slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'PW_SO_LC_OV', label: 'Overview', path: '/dq_pro/solutions/learncreator/overview',
                        slideCode: 'CS-Solution-LC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    {
                        // Sheet note on this row: "Team to create Preview LearnCreator".
                        code: 'PW_SO_LC_PV', label: 'Preview', path: '/dq_pro/solutions/learncreator/preview',
                        slideCode: 'CS-Solution-LC-preview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                ],
            },
            {
                // Sheet codes this P0G; the architecture deck calls the solution GC.
                code: 'PW_SO_P0G', label: 'COS Government Checklist', path: '/dq_pro/solutions/government-checklist',
                slideCode: 'CS-Solution-GC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'PW_SO_P0G_OV', label: 'Overview', path: '/dq_pro/solutions/government-checklist/overview',
                        slideCode: 'CS-Solution-GC-overview', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    // No Preview: there is no Government Checklist preview yet,
                    // so the entry is removed (bug sheet, Ian 23 Sep).
                ],
            },
        ],
    },

    {
        code: 'PW_GO',
        label: 'Growth Opportunities',
        icon: TrendingUpIcon,
        path: '/dq_pro/growth-opportunities',
        slideCode: 'PW-ALL-Growth-Pipeline',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
        children: [
            {
                code: 'PW_GO_OP',
                /** Tutorial anchor (Partner workspace, Opportunity Pipeline step). */
                id: 'sidebar-opportunity-pipeline-link',
                label: 'Opportunity Pipeline',
                /**
                 * One destination for every plan, deliberately.
                 *
                 * An earlier version sent enterprise_p1 to the existing
                 * program-requests list and everyone else here. That mapping was
                 * a guess: the sheet's pipeline carries unlock, credit, program
                 * and recommendation requests together, and only program requests
                 * exist — so the requests list is a fragment of this page, not
                 * this page. Whether it belongs behind this row is a product
                 * decision, not one to infer.
                 *
                 * The requests list is unchanged and still reachable at its own
                 * URL for the plan that has it.
                 */
                path: '/dq_pro/growth-opportunities/pipeline',
                slideCode: 'PW-ALL-Growth-Pipeline',
                solutions: [SOLUTION.ALL],
                visibleWhen: () => true,
            },
            {
                code: 'PW_GO_PA', label: 'Product Adoption', path: '/dq_pro/growth-opportunities/product-adoption',
                slideCode: 'PW-ALL-Growth-Solution', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                children: [
                    {
                        code: 'PW_GO_PA_SA', label: 'Solution Adoption',
                        path: '/dq_pro/growth-opportunities/product-adoption/solution-adoption',
                        slideCode: 'PW-ALL-Growth-Solution', solutions: [SOLUTION.ALL], visibleWhen: () => true,
                    },
                    {
                        code: 'PW_GO_PA_PA', label: 'My Program Adoption',
                        path: '/dq_pro/growth-opportunities/product-adoption/my-program-adoption',
                        slideCode: 'PW-P1B, LC-Growth-MyProgram',
                        // Sheet tags this P1B, LC — narrower than its sibling's ALL.
                        solutions: [SOLUTION.P1B, SOLUTION.LC],
                        // P1B is P1 with LearnCreator distributed, not a plan of its
                        // own, so this follows product access. The old test listed
                        // ENTERPRISE_P1B, a plan key no account carries.
                        visibleWhen: (ctx) => isProductEnabled(ctx.authUser, PRODUCT_KEY.LEARNING_PROGRAM),
                    },
                ],
            },
            {
                code: 'PW_GO_PS', label: 'Portfolio Snapshot', path: '/dq_pro/growth-opportunities/portfolio-snapshot',
                slideCode: 'PW-{P0|P1|LC}-Growth-PortfolioKPI',
                // Sheet tags this P0, P1, LC — not ALL, unlike its two siblings.
                solutions: [SOLUTION.P0, SOLUTION.P1, SOLUTION.LC],
                visibleWhen: () => true,
                /**
                 * The sheet gives these two Level 3 NAMES (rows 24–27, column P)
                 * but no Level 3 codes, unlike Product Adoption's children. The
                 * codes below follow the sheet's own convention so the renderer
                 * has a stable key.
                 *
                 * They point at named pages of their own, NOT at the Partner
                 * Dashboard's existing "Executive Summary" and "Attention Needed"
                 * screens, even though the names line up almost exactly.
                 *
                 * An earlier version did link there. That was wrong: the
                 * Dashboards sheet restructures the Partner Dashboard into
                 * Introduction / Baseline Results / Learning Programs /
                 * Methodology / Reassessment, and neither Executive Summary nor
                 * Attention Needed survives as a Level 1 — they are listed in its
                 * "OLD" columns. Linking Growth Opportunities into pages the
                 * design is retiring would have to be undone.
                 *
                 * Gap Register G063 covers this directly: workspace slide codes
                 * point at dashboard ENTRY POINTS, not deep-linked sections, and
                 * that is an open decision.
                 */
                children: [
                    {
                        code: 'PW_GO_PS_EO',
                        // Sheet spells this "Exective Overview" — taken as a typo.
                        label: 'Executive Overview',
                        path: '/dq_pro/growth-opportunities/portfolio-snapshot/executive-overview',
                        slideCode: 'PW-{P0|P1|LC}-Growth-PortfolioKPI',
                        // Rows 24, 25 and 26 tag it P0, P1 and LC respectively.
                        solutions: [SOLUTION.P0, SOLUTION.P1, SOLUTION.LC],
                        visibleWhen: () => true,
                    },
                    {
                        code: 'PW_GO_PS_AN',
                        label: 'Attention Needed',
                        path: '/dq_pro/growth-opportunities/portfolio-snapshot/attention-needed',
                        slideCode: 'PW-P0, P1-Growth-PortfolioAttention',
                        // Row 27 tags it P0, P1 — no LC, unlike its sibling. "P1"
                        // means the whole family (base plan + P1A + P1B), not
                        // split by product access.
                        solutions: [SOLUTION.P0, SOLUTION.P1],
                        visibleWhen: (ctx) => [
                            DQ_PRO_PLAN_KEY.ENTERPRISE_P0,
                            DQ_PRO_PLAN_KEY.ENTERPRISE_P1,
                            DQ_PRO_PLAN_KEY.ENTERPRISE_P1A,
                            DQ_PRO_PLAN_KEY.ENTERPRISE_P1B,
                        ].includes(ctx.authUser?.dq_pro_plan?.plan_key),
                    },
                ],
            },
        ],
    },

    {
        code: 'PW_MS',
        /** Tutorial anchor (My Schools step). */
        id: 'sidebar-my-schools-link',
        /**
         * Always "My Schools". The sheet carries one label for this row and
         * "Client List" appears nowhere in it — that was the old wording, kept
         * conditionally at first and now dropped, so every plan sees the name
         * the sheet specifies.
         */
        label: 'My Schools',
        icon: SchoolIcon,
        path: '/dq_pro/clients/list',
        slideCode: 'PW-ALL-My Schools-Main',
        solutions: [SOLUTION.ALL],
        visibleWhen: (ctx) => Number(ctx.authUser?.access_level) === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL
            || (ctx.authUser?.dq_pro_plan?.status === 'active'
                && ENTERPRISE_PLAN_KEYS.includes(ctx.authUser?.dq_pro_plan?.plan_key)),
    },

    {
        code: 'PW_MP',
        label: 'My Programs',
        icon: EmojiObjectsIcon,
        // Parent opens the list when the drawer is collapsed.
        path: '/dq_pro/learning/program/list',
        slideCode: 'PW-P1, LC-MyPrograms',
        solutions: [SOLUTION.P1, SOLUTION.LC],
        // Today's real gate: any program or assessment product.
        visibleWhen: (ctx) => ctx.hasAnyProgramAccess,
        note: 'Was "Your Programs". The sheet moves Create and Learning Programs in here.',
        children: [
            {
                code: 'PW_MP_CR',
                label: 'Create',
                path: '/dq_pro/create',
                // LC only — P1 partners don't author programs via this wizard.
                solutions: [SOLUTION.LC],
                visibleWhen: (ctx) => ctx.hasAnyProgramAccess,
                note: 'Sheet: "Move: inside My Programs with the list of programs created".',
            },
            {
                code: 'PW_MP_LP',
                id: 'sidebar-products-link',
                label: 'Learning Programs',
                path: '/dq_pro/learning/program/list',
                solutions: [SOLUTION.P1, SOLUTION.LC],
                visibleWhen: (ctx) => ctx.hasLearningPrograms,
                note: 'Sheet: "Move: inside My Programs with LearnCreator".',
            },
        ],
    },

    {
        code: 'PW_LC',
        label: 'LearnCreator',
        icon: CreateIcon,
        // The LearnCreator landing page, which every entry point opens; typing
        // a goal there starts the builder (bug sheet Ian-LC-30Sep-1).
        path: LEARNCREATOR_START_PATH,
        slideCode: 'PW-P1, LC-LearnCreator',
        solutions: [SOLUTION.P1, SOLUTION.LC],
        // Same product gate as My Programs — the sheet tags both P1, LC.
        visibleWhen: (ctx) => ctx.hasAnyProgramAccess,
    },
    {
        code: 'PW_IN',
        label: 'Insights',
        icon: BarChartIcon,
        path: (ctx) => ctx.insightsPath,
        slideCode: 'PW-ALL-Insights-My Dashboard',
        solutions: [SOLUTION.ALL],
        visibleWhen: () => true,
        children: [
            {
                code: 'PW_IN_PD',
                label: 'My Dashboard',
                // Resolves to the Partner Dashboard when that feature is on —
                // which is what the deck calls My Dashboard.
                path: (ctx) => ctx.insightsPath,
                slideCode: 'PW-ALL-Insights-My Dashboard',
                solutions: [SOLUTION.ALL],
                visibleWhen: () => true,
                note: 'Was "Data & Insights".',
            },
            {
                code: 'PW_IN_ND',
                /**
                 * The sheet's Level 2 Name is "National Dashboard (Locked)" —
                 * the ONLY row where it writes a state into the name. This is
                 * the one place the menu text differs from the sheet, and
                 * deliberately: "(Locked)" would be wrong for a partner who has
                 * the dashboard, and the row is hidden outright for one who does
                 * not. Whether unentitled rows should show Locked instead of
                 * disappearing is the open hide-vs-Locked question; if the answer
                 * is "show Locked", this label becomes conditional.
                 */
                label: 'National Dashboard',
                /**
                 * One National dashboard per partner × country (6 Oct 2026; SG
                 * workshop, Aug): with several countries the row is a PARENT with
                 * one child per country, like the school menu's Class Dashboard →
                 * Class 1, 2, …. A child SELECTS its country (DashboardContext,
                 * kept per partner in sessionStorage — nothing in the URL, user
                 * decision) and opens the overview; the dashboard pages read the
                 * selection. With one country the row stays a single link that
                 * selects it; with none (list not loaded, or no completed
                 * assessment yet) it is the plain link it always was and the page
                 * explains. MainList puts `nationalRows`, `nationalCountry` and
                 * `selectNationalCountry` on the context.
                 */
                path: NATIONAL_OVERVIEW,
                onSelect: (ctx) => {
                    const rows = ctx.nationalRows || [];
                    if (rows.length === 1 && ctx.selectNationalCountry) ctx.selectNationalCountry(rows[0].country);
                },
                children: (ctx) => {
                    const rows = ctx.nationalRows || [];
                    if (rows.length < 2) return [];
                    return rows.map((r) => ({
                        code: `PW_IN_ND_${r.country}`,
                        label: countryName(r.country),
                        path: NATIONAL_OVERVIEW,
                        onSelect: (c) => c.selectNationalCountry && c.selectNationalCountry(r.country),
                        activeWhen: (c) => c.nationalCountry === r.country,
                        solutions: [SOLUTION.ALL],
                        visibleWhen: () => true,
                    }));
                },
                // The sheet writes the NAME as "National Dashboard (Locked)", but
                // the deck's slide is plain "PW-ALL-Insights-National Dashboard".
                // "(Locked)" is a state, not part of the code — Gap Register G103.
                slideCode: 'PW-ALL-Insights-National Dashboard',
                solutions: [SOLUTION.ALL],
                /**
                 * Tagged ALL in the sheet but still feature-gated. The server
                 * already decides per role who may see this and strips it from
                 * feature_access, so the menu must respect that rather than
                 * re-deriving it from the solution tag.
                 */
                visibleWhen: (ctx) => isFeatureEnabled(ctx.authUser, FEATURE_KEY.NATIONAL_DASHBOARD),
                note: 'Sheet label carries "(Locked)" — taken as literal text; no lock behaviour is specified.',
            },
        ],
    },

    /*
     * REMOVED: "Your classes".
     *
     * It is not in the Partner block of the Navigation Bar sheet — classes
     * belong to the Network, School and Teacher workspaces. It was carried over
     * from the old sidebar and kept here on the assumption that removing it
     * would strand level-21 accounts; the sheet is the specification, so it is
     * gone.
     *
     * The page itself is untouched, and /dq_pro/classes still resolves for any
     * account the route guard admits.
     */



    /*
     * MOVED: "Account settings".
     *
     * Sheet row 19 — 'Move: to "Profile Icon" field; Rewording: "Account
     * Settings" > "Settings"'. It now sits in the avatar menu as "Settings",
     * pointing at the same page, so the move costs nothing.
     *
     * MOVED: "Billing" (PW_BI).
     *
     * Sheet row 18 — 'Move: inside "Billing and Payments" under "Settings"'.
     * Done: the Settings page now has that tab and renders the billing screen
     * inside it, alongside subscription, payment and credits. Ian confirmed on
     * 20 Aug that everything billing-related belongs together there.
     *
     * /dq_pro/invoices/list still resolves, so old links and bookmarks work.
     */

    {
        code: 'PW_SP',
        label: 'Support',
        icon: HelpOutlineIcon,
        path: '/support',
        slideCode: 'PW-ALL-Support',
        solutions: [SOLUTION.ALL],
        // Access level 20 only (per Ian Ryu's design-review note) — this same
        // nav also renders for a level-21 non-network owner, who would
        // otherwise see a link that the route guard now redirects away from.
        visibleWhen: (ctx) => Number(ctx.authUser?.access_level) === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL,
    },
];

/**
 * Rows the sheet defines that have no page yet.
 *
 * Kept out of the tree rather than rendered disabled: a menu item that cannot go
 * anywhere is worse than one that is not there. Listed so the gap is visible.
 */
export const PARTNER_WORKSPACE_PENDING = [
    { code: 'PW_SO', label: 'Solutions', reason: 'Overview and preview pages not built' },
    { code: 'PW_GO', label: 'Growth Opportunities', reason: 'Needs the requests API' },
];
